import React from 'react';

import { AccessRequestGetResponse, DecisionStatus } from '@ariksa/jit/api';
import {
  Avatar,
  Box,
  Center,
  HStack,
  Input,
  SimpleGrid,
  Stack,
} from '@chakra-ui/react';
import { capitalize } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import {
  formatTime,
  FormFieldBox,
  getIcon,
  getProviderAlias,
  StackedHeader,
} from 'components/DataDisplay';
import {
  CopyToClipboardIconButton,
  defaultFormStyles,
  PrimaryButton,
} from 'components/DataEntry';
import { AutomationIcon, ThumbsDownIcon, ThumbsUpIcon } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { selectUser } from 'containers/App/selectors';
import {
  getJitDecisionEntries,
  getJitInitials,
  getJitOtherApproversLine,
  getJitRequestSourceLabel,
  getJitRoleName,
  getJitWindowTiles,
  jitCaptionStyles,
  jitDecisionColors,
  renderJitDecisionTag,
  renderJitPrivilegeTags,
  renderJitReasonRow,
  renderJitRoleIcon,
  renderJitSection,
} from 'containers/Jit/JitRequests/Components/utils';
import { selectJitRequests } from 'containers/Jit/JitRequests/selectors';
import { actions } from 'containers/Jit/JitRequests/slice';

interface ViewJITRequestProps {
  isOpen: boolean;
  onClose: () => void;
  row: AccessRequestGetResponse;
  viewBy: string;
  /** Reloads the listing the drawer was opened from, filter and page intact. */
  onActionSuccess: () => void;
}

export const ViewJITRequest: React.FC<ViewJITRequestProps> = props => {
  const { isOpen, onClose, row, viewBy, onActionSuccess } = props;
  const dispatch = useDispatch();
  const [reason, setReason] = React.useState('');
  const { jitRequestAction } = useSelector(selectJitRequests);
  const { info } = useSelector(selectUser);
  const otherApprovers = getJitOtherApproversLine(row.approvers, info?.email);

  const requester = row.requester_display_name || row.requester_email;
  const requesterAvatarProps = {
    name: requester,
    getInitials: getJitInitials,
    bg: colorOpacity(customTheme.colors.primary, 0.14),
    color: 'primary',
  };
  const isPending = row.status?.toLowerCase() === 'pending';
  // An expired request was never decided, so it has no decision to show.
  const isDecided = row.decision_status !== DecisionStatus.Pending;
  const roleName = getJitRoleName(row);
  const windowLabel = row.approved_start_time ? 'Approved' : 'Requested';
  const decisionColors = jitDecisionColors[row.decision_status];
  const isDenied = row.decision_status === DecisionStatus.Denied;
  const decisionVerb = isDenied ? 'denied' : 'approved';
  // A denial's reason is not read yet, so its absence would be a guess.
  const noReasonText = isDenied ? undefined : 'No reason given';

  const onSuccess = () => {
    onActionSuccess();
    onClose();
  };

  const approveJitRequest = () => {
    dispatch(
      actions.approveJitRequest({
        q: {
          requestId: row?.request_id,
          approvalGrantedRequest: {
            approved_start_time: row.requested_start_time,
            approved_end_time: row.requested_end_time,
            comment: reason,
          },
        },
        onSuccess,
      }),
    );
  };

  const denyJitRequest = () => {
    dispatch(
      actions.denyJitRequest({
        q: {
          requestId: row?.request_id,
          approvalDeniedRequest: {
            reason,
          },
        },
        onSuccess,
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      styles={{ body: { px: 6 } }}
      header={
        <HStack w="full" pr={6} spacing={4}>
          <Avatar size="md" {...requesterAvatarProps} />
          <StackedHeader
            upper={requester}
            lower={[row.requester_email, row.requester_type]
              .filter(Boolean)
              .join(' · ')}
          />
          <Box flexShrink={0}>
            {/* An approver's queue shows a pending request as work for them. */}
            {renderJitDecisionTag(
              row,
              viewBy === 'requests' ? undefined : 'For Review',
            )}
          </Box>
        </HStack>
      }
      body={
        <Stack spacing={5}>
          <FormFieldBox px={4} py={3}>
            <Stack spacing={3}>
              <HStack spacing={3}>
                <Center boxSize={7} flexShrink={0}>
                  {getIcon(row.cloud_type)}
                </Center>
                <Stack spacing={0}>
                  <Box fontSize="lg">
                    {row.access_type} access
                    {row.data_group_name && ` to ${row.data_group_name}`}
                  </Box>
                  <Box {...jitCaptionStyles} fontSize="14px">
                    {getProviderAlias(row.cloud_type)} · {row.cloud_account_id}
                  </Box>
                </Stack>
              </HStack>
              {renderJitPrivilegeTags(row)}
              <HStack
                spacing={2}
                pt={3}
                borderTop="1px solid"
                borderColor="componentBorder"
              >
                {renderJitRoleIcon(row)}
                <Box
                  flex={1}
                  minW={0}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  color={roleName ? 'black' : customTheme.colors.gray['250']}
                >
                  {roleName ||
                    (isPending ? 'Custom role · created on approval' : '-')}
                </Box>
                {roleName && (
                  <CopyToClipboardIconButton
                    value={roleName}
                    tooltip="Copy role name"
                    iconSize={4}
                  />
                )}
              </HStack>
            </Stack>
          </FormFieldBox>
          {renderJitSection(
            `${windowLabel} window`,
            <SimpleGrid columns={3} spacing={3}>
              {getJitWindowTiles(row).map(({ label, value, note, isLive }) => (
                <FormFieldBox
                  key={label}
                  px={3}
                  py={2.5}
                  {...(isLive && {
                    borderColor: colorOpacity(customTheme.colors.primary, 0.35),
                    bg: colorOpacity(customTheme.colors.primary, 0.06),
                  })}
                >
                  <Stack spacing={0.5}>
                    <Box
                      fontSize="12px"
                      color={
                        isLive ? 'primary' : customTheme.colors.gray['250']
                      }
                    >
                      {label}
                    </Box>
                    <Box fontSize="17px" color={isLive ? 'primary' : 'black'}>
                      {value}
                    </Box>
                    {note && <Box {...jitCaptionStyles}>{note}</Box>}
                  </Stack>
                </FormFieldBox>
              ))}
            </SimpleGrid>,
          )}
          {renderJitSection(
            'Reason for Access',
            renderJitReasonRow(
              <Avatar size="sm" {...requesterAvatarProps} />,
              requester,
              `requested · ${formatTime(row.request_creation_time)} · via ` +
                getJitRequestSourceLabel(row.request_source),
              row.reason,
            ),
          )}
          {isDecided &&
            renderJitSection(
              'Reason for Decision',
              <Stack spacing={3}>
                {getJitDecisionEntries(row).map(({ name, comment, isAuto }) => (
                  <React.Fragment key={name ?? decisionVerb}>
                    {renderJitReasonRow(
                      <Avatar
                        size="sm"
                        name={isAuto ? undefined : name}
                        getInitials={getJitInitials}
                        icon={
                          isAuto ? <AutomationIcon boxSize={4} /> : undefined
                        }
                        bg={decisionColors.bg}
                        color={decisionColors.color}
                      />,
                      name,
                      <>
                        <Box as="span" color={decisionColors.color}>
                          {name ? decisionVerb : capitalize(decisionVerb)}
                        </Box>
                        {row.request_approval_time &&
                          ` · ${formatTime(row.request_approval_time)}`}
                      </>,
                      comment,
                      noReasonText,
                    )}
                  </React.Fragment>
                ))}
              </Stack>,
            )}
          {isPending &&
            renderJitSection(
              'Reason for Decision',
              <Stack spacing={1}>
                <Input
                  {...defaultFormStyles.textField?.input}
                  py={3.5}
                  onChange={e => setReason(e.target.value)}
                  value={reason}
                  placeholder="Enter reason for decision"
                />
                {otherApprovers && (
                  <Box {...jitCaptionStyles}>{otherApprovers}</Box>
                )}
              </Stack>,
            )}
        </Stack>
      }
      footer={
        isPending ? (
          <HStack>
            <PrimaryButton
              bg={colorOpacity(customTheme.colors.critical, 0.2)}
              color="black"
              _hover={{}}
              border="none"
              leftIcon={<ThumbsDownIcon />}
              isDisabled={jitRequestAction.isLoading}
              isLoading={jitRequestAction.isLoading}
              onClick={denyJitRequest}
            >
              DENY
            </PrimaryButton>
            <PrimaryButton
              bg="#d2f8e9"
              color="black"
              _hover={{}}
              border="none"
              leftIcon={<ThumbsUpIcon />}
              isDisabled={jitRequestAction.isLoading}
              isLoading={jitRequestAction.isLoading}
              onClick={approveJitRequest}
            >
              APPROVE
            </PrimaryButton>
          </HStack>
        ) : (
          <PrimaryButton px={4} onClick={onClose}>
            OK
          </PrimaryButton>
        )
      }
    />
  );
};
